import ExcelJS from 'exceljs';
import {
    calculateProductDifference,
    calculateTotals,
    groupProductsByCategory,
    type ProductWithCountAndNeeded,
} from './prepareReceptionData';

// Re-export for backwards compatibility
export type { ProductWithCountAndNeeded };

/**
 * Applies styling to a category row
 */
const styleCategoryRow = (row: ExcelJS.Row) => {
    row.font = { bold: true, size: 12 };
    row.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' },
    };
};

/**
 * Applies styling to the header row
 */
const styleHeaderRow = (row: ExcelJS.Row) => {
    row.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    row.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' },
    };
};

/**
 * Downloads the generated Excel file
 */
const downloadExcelFile = (content: ArrayBuffer, storeName: string, receptionDate: string) => {
    const url = URL.createObjectURL(new Blob([content]));
    const link = document.createElement('a');
    link.href = url;
    const sanitizedStoreName = storeName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    link.download = `reception-${sanitizedStoreName}-${receptionDate}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
};

/**
 * Generates and downloads a reception file in Excel format with products grouped by category.
 * Includes columns for product name, EAN13, requested quantity, delivered quantity, and difference.
 */
export const writeReceptionFile = async (
    productsWithCountAndNeeded: ProductWithCountAndNeeded[],
    storeName: string,
    receptionDate: string,
    storeLabel?: string
) => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Bon de réception');

    // Define columns (order: Store moved to 3rd position, before Requested)
    sheet.columns = [
        { header: 'Nom du produit', key: 'name', width: 35 },
        { header: 'EAN 13', key: 'ean13', width: 20 },
        { header: 'Boutique', key: 'store', width: 20 },
        { header: 'Demandés', key: 'needed', width: 15 },
        { header: 'Livrés', key: 'delivered', width: 15 },
        { header: 'Écart', key: 'difference', width: 15 },
    ];

    // Group products by category
    const categories = groupProductsByCategory(productsWithCountAndNeeded);

    // Add products grouped by category in order of appearance
    categories.forEach((category) => {
        // Add category row
        const categoryRow = sheet.addRow({
            name: category.name.toUpperCase(),
            ean13: '',
            needed: '',
            delivered: '',
            difference: '',
            store: '',
        });
        styleCategoryRow(categoryRow);

        // Add products for this category
        category.products.forEach((product) => {
            const row = sheet.addRow({
                name: product.label,
                ean13: product.reference,
                needed: product.needed,
                delivered: product.count,
                difference: calculateProductDifference(product),
                store: storeLabel || '',
            });
            row.getCell('difference').alignment = { horizontal: 'right' };
        });

        // Add category total rows
        const categoryTotals = calculateTotals(category.products);
        const categoryTotalRow = sheet.addRow({
            name: `Total ${category.name}`,
            ean13: '',
            needed: categoryTotals.totalNeeded,
            delivered: categoryTotals.totalDelivered,
            difference: categoryTotals.totalDifference,
            store: '',
        });
        categoryTotalRow.font = { bold: true };
        categoryTotalRow.getCell('difference').alignment = { horizontal: 'right' };

        // Add category surplus row
        const categorySurplusRow = sheet.addRow({
            name: `Surplus ${category.name}`,
            ean13: '',
            needed: '',
            delivered: '',
            difference: categoryTotals.totalSurplus,
            store: '',
        });
        categorySurplusRow.font = { bold: true };
        categorySurplusRow.getCell('difference').alignment = { horizontal: 'right' };

        // Add category missing row
        const categoryMissingRow = sheet.addRow({
            name: `Manquant ${category.name}`,
            ean13: '',
            needed: '',
            delivered: '',
            difference: categoryTotals.totalMissing,
            store: '',
        });
        categoryMissingRow.font = { bold: true };
        categoryMissingRow.getCell('difference').alignment = { horizontal: 'right' };

        // Add category service rate row
        const categoryServiceRateRow = sheet.addRow({
            name: `Taux de service ${category.name}`,
            ean13: '',
            needed: '',
            delivered: '',
            difference: `${(categoryTotals.serviceRate * 100).toFixed(1)}%`,
            store: '',
        });
        categoryServiceRateRow.font = { bold: true };
        categoryServiceRateRow.getCell('difference').alignment = { horizontal: 'right' };
    });

    // Add total row
    const {
        totalDelivered,
        totalNeeded,
        totalDifference,
        totalSurplus,
        totalMissing,
        serviceRate,
    } = calculateTotals(productsWithCountAndNeeded);

    const totalRow = sheet.addRow({
        name: 'TOTAL',
        ean13: '',
        needed: totalNeeded,
        delivered: totalDelivered,
        difference: totalDifference,
        store: '',
    });
    totalRow.font = { bold: true };
    totalRow.getCell('difference').alignment = { horizontal: 'right' };

    // Add total surplus row
    const surplusRow = sheet.addRow({
        name: 'Total surplus',
        ean13: '',
        needed: '',
        delivered: '',
        difference: totalSurplus,
        store: '',
    });
    surplusRow.font = { bold: true };
    surplusRow.getCell('difference').alignment = { horizontal: 'right' };

    // Add total missing row
    const missingRow = sheet.addRow({
        name: 'Total manquant',
        ean13: '',
        needed: '',
        delivered: '',
        difference: totalMissing,
        store: '',
    });
    missingRow.font = { bold: true };

    // Add service rate row
    const serviceRateRow = sheet.addRow({
        name: 'Taux de service',
        ean13: '',
        needed: '',
        delivered: '',
        difference: `${(serviceRate * 100).toFixed(1)}%`,
        store: '',
    });
    serviceRateRow.font = { bold: true };
    serviceRateRow.getCell('difference').alignment = { horizontal: 'right' };

    // Format header row
    styleHeaderRow(sheet.getRow(1));

    // Generate and download the file
    const content = await workbook.xlsx.writeBuffer();
    downloadExcelFile(content, storeName, receptionDate);
};
